import React, { useState, useEffect } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import KPICard from './components/KPICard';
import MultiRailRevenueChart from './components/MultiRailRevenueChart';
import AlertsFeed from './components/AlertsFeed';
import RegionalPerformanceGrid from './components/RegionalPerformanceGrid';
import ComplianceStatusPanel from './components/ComplianceStatusPanel';
import Icon from '../../components/AppIcon';

const ExecutiveOverview = () => {
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setLastUpdate(new Date());
    }, 300000);

    return () => clearInterval(interval);
  }, []);

  const handleRefresh = () => {
    setIsRefreshing(true);
    setTimeout(() => {
      setLastUpdate(new Date());
      setIsRefreshing(false);
    }, 1000);
  };

  const kpiData = [
    {
      title: 'Total Transaction Volume',
      value: '$47.2M',
      change: 12.5,
      trend: 'vs last period',
      icon: 'Activity',
      iconColor: '#2563EB',
      sparklineData: [
        { value: 35 }, { value: 38 }, { value: 42 }, { value: 39 }, 
        { value: 44 }, { value: 46 }, { value: 47.2 }
      ]
    },
    {
      title: 'Revenue (All Rails)',
      value: '$2.8M',
      change: 8.3,
      trend: 'vs last period',
      icon: 'DollarSign',
      iconColor: '#10B981',
      sparklineData: [
        { value: 2.1 }, { value: 2.3 }, { value: 2.5 }, { value: 2.4 }, 
        { value: 2.6 }, { value: 2.7 }, { value: 2.8 }
      ]
    },
    {
      title: 'Active User Growth',
      value: '124.5K',
      change: 15.7,
      trend: 'vs last period',
      icon: 'Users',
      iconColor: '#8B5CF6',
      sparklineData: [
        { value: 95 }, { value: 102 }, { value: 108 }, { value: 112 }, 
        { value: 118 }, { value: 121 }, { value: 124.5 }
      ]
    },
    {
      title: 'Platform Uptime',
      value: '99.97%',
      change: 0.02,
      trend: 'vs last period',
      icon: 'Zap',
      iconColor: '#F59E0B',
      sparklineData: [
        { value: 99.95 }, { value: 99.96 }, { value: 99.97 }, { value: 99.96 }, 
        { value: 99.98 }, { value: 99.97 }, { value: 99.97 }
      ]
    }
  ];

  const revenueChartData = [
    { date: 'Jan 12', bitcoin: 145000, ethereum: 98000, stablecoin: 67000, creditCard: 123000 },
    { date: 'Jan 13', bitcoin: 152000, ethereum: 105000, stablecoin: 72000, creditCard: 118000 },
    { date: 'Jan 14', bitcoin: 148000, ethereum: 112000, stablecoin: 78000, creditCard: 125000 },
    { date: 'Jan 15', bitcoin: 165000, ethereum: 108000, stablecoin: 74000, creditCard: 132000 },
    { date: 'Jan 16', bitcoin: 172000, ethereum: 115000, stablecoin: 81000, creditCard: 128000 },
    { date: 'Jan 17', bitcoin: 168000, ethereum: 122000, stablecoin: 85000, creditCard: 135000 },
    { date: 'Jan 18', bitcoin: 178000, ethereum: 118000, stablecoin: 88000, creditCard: 142000 }
  ];

  const alertsData = [
    {
      id: 1,
      severity: 'critical',
      title: 'High Transaction Failure Rate',
      message: 'Bitcoin rail experiencing 8.2% failure rate in EU region. Immediate investigation required.',
      timestamp: '2 min ago',
      action: 'View Details'
    },
    {
      id: 2,
      severity: 'warning',
      title: 'API Response Time Elevated',
      message: 'Average API response time increased to 450ms. Performance optimization recommended.',
      timestamp: '15 min ago',
      action: 'Check Metrics'
    },
    {
      id: 3,
      severity: 'info',
      title: 'Scheduled Maintenance',
      message: 'Ethereum node maintenance scheduled for Jan 20, 2026 at 02:00 UTC. Expected downtime: 30 minutes.',
      timestamp: '1 hour ago',
      action: 'View Schedule'
    },
    {
      id: 4,
      severity: 'success',
      title: 'Conversion Rate Milestone',
      message: 'Checkout conversion rate reached 3.2%, exceeding quarterly target by 12%.',
      timestamp: '2 hours ago',
      action: 'View Report'
    },
    {
      id: 5,
      severity: 'warning',
      title: 'Liquidity Pool Alert',
      message: 'USDC liquidity pool below optimal threshold. Auto-rebalancing triggered.',
      timestamp: '3 hours ago',
      action: 'Monitor Pool'
    },
    {
      id: 6,
      severity: 'info',
      title: 'New Merchant Integration',
      message: 'TechCorp successfully integrated BOLTPay SDK. First transaction processed.',
      timestamp: '4 hours ago',
      action: 'View Merchant'
    }
  ];

  const regionalData = [
    {
      id: 1,
      name: 'North America',
      performance: 97.2,
      volume: '18.5M',
      revenue: '$1.2M',
      activeUsers: '52.3K'
    },
    {
      id: 2,
      name: 'Europe',
      performance: 91.8,
      volume: '14.2M',
      revenue: '$890K',
      activeUsers: '38.7K'
    },
    {
      id: 3,
      name: 'Asia Pacific',
      performance: 95.6,
      volume: '10.8M',
      revenue: '$520K',
      activeUsers: '24.1K'
    },
    {
      id: 4,
      name: 'Latin America',
      performance: 88.4,
      volume: '3.7M',
      revenue: '$190K',
      activeUsers: '9.4K'
    }
  ];

  const complianceData = [
    {
      id: 1,
      category: 'PCI-DSS Compliance',
      status: 'compliant',
      description: 'All card processing systems meet PCI-DSS Level 1 requirements',
      lastCheck: 'Jan 15, 2026',
      nextReview: 'Apr 15, 2026',
      action: null
    },
    {
      id: 2,
      category: 'KYC/AML Verification',
      status: 'warning',
      description: '127 pending verifications require manual review within 48 hours',
      lastCheck: 'Jan 18, 2026',
      nextReview: 'Jan 20, 2026',
      action: 'Review Queue'
    },
    {
      id: 3,
      category: 'SOC 2 Type II',
      status: 'compliant',
      description: 'Security controls audit completed successfully with zero findings',
      lastCheck: 'Dec 20, 2025',
      nextReview: 'Jun 20, 2026',
      action: null
    },
    {
      id: 4,
      category: 'GDPR Data Protection',
      status: 'compliant',
      description: 'Data processing activities comply with EU GDPR requirements',
      lastCheck: 'Jan 10, 2026',
      nextReview: 'Feb 10, 2026',
      action: null
    }
  ];

  const formatTimestamp = (date) => {
    return date?.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  return (
    <MainLayout showControls={true}>
      <div className="max-w-[1400px] mx-auto space-y-6">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">Executive Overview</h1>
            <p className="text-base md:text-lg text-muted-foreground">
              Comprehensive financial performance across all payment rails
            </p>
          </div>

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-muted/30">
              <Icon name="Clock" size={16} className="text-muted-foreground" />
              <div className="flex flex-col">
                <span className="caption text-muted-foreground text-xs">Last Updated</span>
                <span className="text-sm font-medium text-foreground">
                  {formatTimestamp(lastUpdate)}
                </span>
              </div>
            </div>

            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:opacity-90 transition-smooth disabled:opacity-50"
            >
              <Icon 
                name="RefreshCw" 
                size={16} 
                className={isRefreshing ? 'animate-spin' : ''} 
              />
              <span className="text-sm font-medium">Refresh</span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {kpiData?.map((kpi, index) => (
            <KPICard key={index} {...kpi} />
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
          <div className="lg:col-span-2">
            <MultiRailRevenueChart 
              data={revenueChartData}
              onRailFilter={(filters) => console.log('Rail filters:', filters)}
            />
          </div>

          <div className="lg:col-span-1">
            <AlertsFeed alerts={alertsData} />
          </div>
        </div>

        <RegionalPerformanceGrid regions={regionalData} />

        <ComplianceStatusPanel complianceData={complianceData} />

        <div className="card">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-1">Quick Actions</h3>
              <p className="caption text-muted-foreground">Navigate to detailed analytics dashboards</p>
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => window.location.href = '/real-time-operations'}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-muted/30 hover:bg-muted/50 transition-smooth"
              >
                <Icon name="Activity" size={16} className="text-primary" />
                <span className="text-sm font-medium text-foreground">Operations</span>
              </button>

              <button
                onClick={() => window.location.href = '/user-analytics-hub'}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-muted/30 hover:bg-muted/50 transition-smooth"
              >
                <Icon name="Users" size={16} className="text-primary" />
                <span className="text-sm font-medium text-foreground">User Analytics</span>
              </button>

              <button
                onClick={() => window.location.href = '/financial-performance'}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-muted/30 hover:bg-muted/50 transition-smooth"
              >
                <Icon name="DollarSign" size={16} className="text-primary" />
                <span className="text-sm font-medium text-foreground">Financial Performance</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default ExecutiveOverview;